import React, { useState } from 'react';
import { Button, useDialog, useSnackbar } from '@fdha/web-ui-library';
import { Box, useTheme } from '@mui/material';
import {
  SurveyInstance,
  SurveyQuestion,
  SurveyQuestionAnswerInput,
  SurveyQuestionType,
  useAnswerSurveyForSubjectMutation,
} from '@fdha/graphql-api-admin';
import { parseBackendError } from '@fdha/common-utils';
import { useFormik } from 'formik';

import { validateAnswers } from '../../../../utils/answerSurvey';

import SurveyQuestionView from './SurveyQuestionView';
import { Paper } from './AnytimeSurveyPreview';

interface AnswerRequiredSurveyProps {
  surveyInstance: SurveyInstance;
  patientId: string;
  setIsEditing: (isEditing: boolean) => void;
  onSuccess: () => void;
}

interface AnswerSurveyFormValues {
  answers: SurveyQuestionAnswerInput[];
}

const initialValues: AnswerSurveyFormValues = {
  answers: [],
};

const AnswerRequiredSurveys: React.FC<AnswerRequiredSurveyProps> = ({
  surveyInstance,
  patientId,
  setIsEditing,
  onSuccess,
}) => {
  const theme = useTheme();
  const { openDialog, closeDialog } = useDialog();
  const { showSnackbar } = useSnackbar();

  const [answerSurveyMutation] = useAnswerSurveyForSubjectMutation();

  const [answerErrors, setAnswerErrors] = useState<
    {
      questionId: string;
      message: string;
    }[]
  >([]);

  const onSubmit = async (values: AnswerSurveyFormValues) => {
    const { answers } = values;

    // Ensure questions is an array or undefined, but not null
    const questions = surveyInstance.questions || undefined;

    const errors = validateAnswers(answers, questions);

    if (errors.length > 0) {
      setAnswerErrors(errors);
      return;
    }

    try {
      const variables = {
        patientId: patientId,
        instanceId: surveyInstance.id,
        answers,
      };
      await answerSurveyMutation({ variables });
      if (onSuccess) {
        onSuccess();
      }
      setIsEditing(false);
    } catch (error: unknown) {
      const message = parseBackendError(error, 'Error when submitting survey');
      const parsedMessage = JSON.parse(message as string);
      if (parsedMessage.submittedBy) {
        openDialog({
          title: 'Form already submitted',
          content: `This form was already submitted by ${parsedMessage.submittedBy}. Any changes you've made will not be saved. You will be redirected to view the latest submission`,
          confirmButtonLabel: 'View latest form',
          handleConfirm: async () => {
            onSuccess();
            closeDialog();
          },
        });
        return;
      }

      showSnackbar({
        severity: 'error',
        message: 'Error when submitting survey',
      });
      closeDialog();
      console.error('Error submitting survey:', error);
    }
  };

  const { values, isSubmitting, handleSubmit, setFieldValue, setFieldTouched } =
    useFormik({
      initialValues,
      onSubmit,
    });

  const handleCloseAndDiscard = () => {
    setIsEditing(false);
    closeDialog();
  };

  const handleAnswerChange = (
    questionId: string,
    answer: string | string[]
  ) => {
    setFieldValue('answers', [
      ...values.answers.filter(
        (ans: SurveyQuestionAnswerInput) => ans.questionId !== questionId
      ),
      {
        questionId,
        answer: Array.isArray(answer)
          ? answer.filter((ans) => ans !== '')
          : answer !== ''
          ? [answer]
          : [],
      },
    ]);
  };

  const renderQuestion = (item: SurveyQuestion, index: number) => {
    const error = answerErrors.find((error) => error.questionId === item.id);

    return (
      <Paper data-testid="SURVEY_QUESTION_CONTAINER" key={item.id}>
        <SurveyQuestionView
          item={item}
          questionNumber={index}
          onAnswerChange={handleAnswerChange}
          error={error?.message}
        />
      </Paper>
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        data-testid="SURVEY_ANSWER_CONTAINER"
      >
        {surveyInstance?.questions
          ?.filter(
            (question: SurveyQuestion) =>
              question.type !== SurveyQuestionType.Explanation
          )
          .map((item: SurveyQuestion, index: number) =>
            renderQuestion(item, index)
          )}
        <Box
          display="flex"
          gap={2}
          maxWidth="857px"
          width="100%"
          alignSelf="center"
        >
          <Button
            variant="contained"
            color="secondary"
            startEvaIcon={{
              name: 'save-outline',
              size: 'large',
              fill: theme.palette.primary.main,
            }}
            disabled={isSubmitting}
            onClick={() => {
              handleSubmit();
              setFieldTouched('answers');
            }}
          >
            Save Response
          </Button>
          <Button
            variant="contained"
            startEvaIcon={{
              name: 'close-outline',
              size: 'large',
              fill: theme.palette.primary.contrastText,
            }}
            onClick={handleCloseAndDiscard}
          >
            Close and Discard Changes
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AnswerRequiredSurveys;
